import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import AuthShell from '../../ev-journey/components/AuthShell'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import z from 'zod'
import client from '@/http/customer-client'
import { restApiErrorHandler } from '@/lib/restApiErrorHandler'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Button } from '@/components/customer-ui/button'
import i18n from '@/i18n'
import { PasswordInput } from '@/components/custom/password-input'
import { ArrowLeftIcon, Loader2 } from 'lucide-react'

export interface ResetPassword {
  token: string
  email: string
  password: string
  password_confirmation: string
}

async function apiResetPassword(form: ResetPassword) {
  return (
    await client.post<{ message: string }>('/app-api/reset-password', form)
  ).data
}

async function apiValidateResetToken(email: string, token: string) {
  return (
    await client.post<{ message: string }>(
      '/app-api/validate-password-reset-token',
      {
        email,
        token
      }
    )
  ).data
}

const formSchema = z
  .object({
    email: z.email(i18n.t('page_login_customer.validation_invalid_email')),
    token: z
      .string()
      .min(1, i18n.t('page_login_customer.validation_reset_code_required')),
    password: z
      .string()
      .min(4, i18n.t('validation.password.min', { min: 4 }))
      .regex(/[a-zA-Z]/, i18n.t('validation.password.letter'))
      .regex(/\d/, i18n.t('validation.password.number')),
    password_confirmation: z
      .string()
      .min(4, i18n.t('validation.password.min', { min: 4 }))
      .regex(/[a-zA-Z]/, i18n.t('validation.password.letter'))
      .regex(/\d/, i18n.t('validation.password.number'))
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: i18n.t('page_login_customer.validation_passwords_not_match'),
    path: ['password_confirmation']
  })

export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const email = searchParams.get('email') || ''
  const token = searchParams.get('token') || ''

  const { isPending: isValidating, isError: isTokenInvalid } = useQuery({
    queryKey: ['validate-reset-token', email, token],
    queryFn: () => apiValidateResetToken(email, token),
    retry: false
  })

  const resetPasswordMutation = useMutation({
    mutationFn: async (credentials: z.infer<typeof formSchema>) => {
      const form = {
        token: token,
        email: email,
        password: credentials.password,
        password_confirmation: credentials.password_confirmation
      }
      return await apiResetPassword(form)
    },
    onError: restApiErrorHandler,
    onSuccess: () => {
      void navigate('/customer/login')
      toast.success(t('page_reset_password.toast_successful_title'))
    }
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      password_confirmation: '',
      email: email,
      token: token
    }
  })

  function onSubmit(formData: z.infer<typeof formSchema>) {
    resetPasswordMutation.mutate(formData)
  }

  if (isValidating) {
    return (
      <AuthShell
        title='Wachtwoord opnieuw instellen'
        subtitle='Even geduld aub, we controleren je link...'
      >
        <div className='flex items-center justify-center p-8'>
          <Loader2 className='text-primary h-8 w-8 animate-spin' />
        </div>
      </AuthShell>
    )
  }

  if (isTokenInvalid) {
    return (
      <AuthShell
        title={t('validation.password.reset_token_invalid_title', {
          defaultValue: 'Ongeldige of verlopen link'
        })}
        subtitle={t('validation.password.reset_token_invalid_description', {
          defaultValue:
            'De link voor het opnieuw instellen van het wachtwoord is ongeldig of verlopen. Vraag een nieuwe aan.'
        })}
      >
        <div className='grid gap-6 text-center'>
          <Link
            to='/customer/login'
            className='flex items-center justify-center gap-1 text-sm underline decoration-solid hover:opacity-75'
          >
            <ArrowLeftIcon className='h-4 w-4' />
            {t('button.back_to_log_in')}
          </Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title='Wachtwoord opnieuw instellen'
      subtitle='Voer een nieuw wachtwoord in om je account te beveiligen.'
    >
      <Form {...form}>
        <form onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>
                    {t('page_reset_password.form_password')}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput placeholder='********' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password_confirmation'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>
                    {t('page_reset_password.form_password_confirmation')}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput placeholder='********' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type='submit'
              className='mt-2'
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              <span>{t('button.submit')}</span>
            </Button>
            <Link
              to='/customer/login'
              className='flex items-center justify-center gap-1 text-sm underline decoration-solid hover:opacity-75'
            >
              {t('button.back_to_log_in')}
            </Link>
          </div>
        </form>
      </Form>
    </AuthShell>
  )
}
